/** @file Two-point turn and ratio: reference vs real segment in the anisotropic metric W (design 21.6). */
import { screenAngle } from '@zilo/pose3d-render-core/camera/screenAngle';
import { wrapSignedDegrees } from '@zilo/pose3d-render-core/camera/wrapSignedDegrees';

/** `theta` turns the quad (degrees, counter-clockwise as seen); `ratio = |real| / |ref|`. */
export type TwoPointFrame = { theta: number; ratio: number };

const EPS = 1e-9;

/**
 * `W = diag(2 H_vp / W_vp, 1)` (1.125 for 640 x 360); `ref = W (pivot2 - pivot)` on the unrotated quad (image px =
 * screen px, y down), `real = W (B' - A')`; `ratio = |real| / |ref|` (1 when ref is degenerate); theta = the signed
 * angle from ref to real, wrapped to [-180, 180) (0 when either is shorter than 1e-9 px).
 */
export function twoPointFrameOf(aScreen: [number, number], bScreen: [number, number], pivot: [number, number], pivot2: [number, number], viewport: [number, number]): TwoPointFrame {
  const wx = (2 * viewport[1]) / viewport[0];
  const ref: [number, number] = [(pivot2[0] - pivot[0]) * wx, pivot2[1] - pivot[1]];
  const real: [number, number] = [(bScreen[0] - aScreen[0]) * wx, bScreen[1] - aScreen[1]];
  const refLen = Math.hypot(ref[0], ref[1]);
  const realLen = Math.hypot(real[0], real[1]);
  const ratio = refLen < EPS ? 1 : realLen / refLen;
  const theta = refLen < EPS || realLen < EPS ? 0 : wrapSignedDegrees(screenAngle(real[0], real[1]) - screenAngle(ref[0], ref[1]));
  return { theta, ratio };
}
